import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { PlanComparison } from '../components/payments/PlanComparison';
import { BillingHistory } from '../components/payments/BillingHistory';
import { subscriptionService, type Subscription } from '../services/subscription.service';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/shared/Button';
import { getPlanLabel } from '../utils/planLabels';
import { formatEuroAmount, getSubscriptionPlan, isPassesModel } from '../config/pricing';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  Settings, 
  ArrowUp, 
  ArrowDown,
  AlertCircle,
  User,
  RefreshCw
} from 'lucide-react';

export const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState('Free');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const hasPaidSubscription = !!subscription?.plan && subscription?.status === 'active';

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    setIsLoading(true);
    try {
      const sub = await subscriptionService.getStatus();
      setSubscription(sub);
      setCurrentPlan(sub.plan || 'Free');
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
    setIsLoading(false);
  };

  const handleUpgrade = async () => {
    if (isPassesModel) {
      // Pass checkout does not exist yet: Stripe is still on the subscription
      // model, so send the user to the pricing page instead of faking a purchase.
      navigate('/pricing');
      return;
    }

    const plans = ['level_bundle', 'full_access'];
    const currentIndex = plans.indexOf(currentPlan);

    // Free user has no paid subscription to mutate — use checkout flow
    if (!hasPaidSubscription) {
      navigate('/checkout?plan=level_bundle');
      return;
    }
    if (currentIndex < plans.length - 1) {
      const newPlan = plans[currentIndex + 1];
      await handleChangePlan(newPlan);
    }
  };

  const handleDowngrade = async () => {
    const plans = ['level_bundle', 'full_access'];
    const currentIndex = plans.indexOf(currentPlan);
    if (currentIndex > 0) {
      const newPlan = plans[currentIndex - 1];
      await handleChangePlan(newPlan);
    }
  };

  const handleChangePlan = async (newPlan: string) => {
    const confirmed = window.confirm(
      t('subscription.confirmChange', { plan: getPlanLabel(newPlan, t) })
    );
    
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      await subscriptionService.changeSubscription(newPlan);
      setCurrentPlan(newPlan);
      await loadSubscription();
    } catch (error) {
      alert(t('subscription.updateError'));
    }
    setIsUpdating(false);
  };

  const handleCancel = async () => {
    const confirmed = window.confirm(t('subscription.confirmCancel'));
    
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      await subscriptionService.cancelSubscription();
      await loadSubscription();
    } catch (error) {
      alert(t('subscription.cancelError'));
    }
    setIsUpdating(false);
  };

  const handleReactivate = async () => {
    const confirmed = window.confirm(t('subscription.reactivateConfirm'));
    if (!confirmed) return;
    setIsUpdating(true);
    try {
      await subscriptionService.reactivateSubscription();
      await loadSubscription();
    } catch (error) {
      alert(t('subscription.reactivateError'));
    }
    setIsUpdating(false);
  };

  const handleManageBilling = async () => {
    if (!hasPaidSubscription) {
      // Pass checkout does not exist yet; Stripe still bills subscriptions.
      navigate(isPassesModel ? '/pricing' : '/checkout?plan=level_bundle');
      return;
    }

    try {
      const { url } = await subscriptionService.getPortalLink();
      window.open(url, '_blank');
    } catch (error) {
      alert(t('subscription.updateError'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <div className="flex items-center justify-center gap-3 text-gray-500">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            <span className="text-lg">{t('subscription.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">{t('subscription.pageTitle')}</h1>
            <p className="text-gray-600 mt-1">{t('subscription.pageSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">
            <User className="w-4 h-4" />
            <span>{user?.email || ''}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{t('subscription.currentSubscription')}</h2>
                <p className="text-gray-600">{t('subscription.manageSubscription')}</p>
              </div>
            </div>

            <span className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold self-start ${
              subscription?.cancelAtPeriodEnd
                ? 'bg-orange-100 text-orange-700'
                : 'bg-green-100 text-green-700'
            }`}>
              <CheckCircle className="w-5 h-5" />
              {subscription?.cancelAtPeriodEnd
                ? t('subscription.cancelledStatus', {
                    date: subscription.currentPeriodEnd
                      ? new Date(subscription.currentPeriodEnd).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
                      : '',
                  })
                : (subscription?.status === 'active'
                    ? t('subscription.active')
                    : (subscription?.status || t('subscription.active')))}
            </span>

                    {/* Dutch-law required: show access-end date clearly when cancelled */}
                    {subscription?.cancelAtPeriodEnd && subscription.currentPeriodEnd && (
                      <div className="w-full mt-4 flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                        <span>
                          {t('subscription.cancelledUntil', {
                            date: new Date(subscription.currentPeriodEnd).toLocaleDateString('nl-NL', {
                              day: 'numeric', month: 'long', year: 'numeric',
                            }),
                          })}
                        </span>
                      </div>
                    )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="p-5 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
              <p className="text-sm text-gray-600 mb-1">{t('subscription.plan')}</p>
              <p className="text-3xl font-bold text-primary">
                {getPlanLabel(currentPlan, t)}
              </p>
              {currentPlan === 'level_bundle' && (
                <span className="inline-block mt-2 text-xs bg-secondary text-white px-2 py-0.5 rounded-full">
                  {t('subscription.mostChosen')}
                </span>
              )}
            </div>

            <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">{t('subscription.amountPerMonth')}</p>
              <p className="text-3xl font-bold">
                {`\u20ac${formatEuroAmount(getSubscriptionPlan(currentPlan)?.price ?? 0)}`}
              </p>
              {currentPlan !== 'Free' && currentPlan !== null && (
                <p className="text-xs text-gray-500 mt-1">{t('subscription.inclVAT')}</p>
              )}
            </div>

            <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('subscription.nextBillingDate')}
              </p>
              <p className="text-xl font-semibold">
                {subscription?.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  : '-'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              size="small"
              icon={<ArrowUp className="w-4 h-4" />}
              onClick={handleUpgrade}
              disabled={currentPlan === 'full_access' || isUpdating || !!subscription?.cancelAtPeriodEnd}
            >
              {t('subscription.upgradePlan')}
            </Button>

            <Button
              variant="secondary"
              size="small"
              icon={<ArrowDown className="w-4 h-4" />}
              onClick={handleDowngrade}
              disabled={currentPlan === 'level_bundle' || currentPlan === 'Free' || currentPlan === null || isUpdating || !!subscription?.cancelAtPeriodEnd}
            >
              {t('subscription.downgrade')}
            </Button>

            <Button
              variant="secondary"
              size="small"
              icon={<Settings className="w-4 h-4" />}
              onClick={handleManageBilling}
              disabled={!hasPaidSubscription}
            >
              {t('subscription.changePaymentMethod')}
            </Button>

            {subscription?.cancelAtPeriodEnd ? (
              <Button
                variant="primary"
                size="small"
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={handleReactivate}
                disabled={isUpdating}
              >
                {t('subscription.reactivate')}
              </Button>
            ) : subscription?.status === 'active' ? (
              <Button
                variant="tertiary"
                size="small"
                icon={<AlertCircle className="w-4 h-4" />}
                onClick={handleCancel}
                disabled={isUpdating}
              >
                {t('subscription.cancelSubscription')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">{t('subscription.comparePlans')}</h2>
          <PlanComparison
            currentPlan={currentPlan}
            onChangePlan={handleChangePlan}
          />
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-6">{t('subscription.billingHistory')}</h2>
          <BillingHistory />
        </div>

        <div className="mt-10 p-6 bg-primary/5 rounded-2xl border border-primary/20 text-center">
          <h3 className="font-bold text-lg mb-2">{t('subscription.needHelp')}</h3>
          <p className="text-gray-600 mb-4">
            {t('subscription.helpText')}
          </p>
          <Button variant="secondary" onClick={() => navigate('/contact')}>
            {t('subscription.contactUs')}
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubscriptionPage;
